import { isObject, TypeGuard } from '@principle-theorem/shared';
import { isString, isBoolean } from 'lodash';

export enum PmsType {
  Exact = 'exact',
  D4W = 'd4w',
  Praktika = 'praktika',
  CorePractice = 'corePractice',
  PracticeWorks = 'practiceWorks',
  Oasis = 'oasis',
  Dentrix = 'dentrix',
}

export enum DataUploadTarget {
  Source = 'source',
  Converted = 'converted',
}

export const PMS_TYPE_FROM_NAME: Record<string, PmsType> = {
  Exact: PmsType.Exact,
  D4W: PmsType.D4W,
  Praktika: PmsType.Praktika,
  'Core Practice': PmsType.CorePractice,
  'Practice Works': PmsType.PracticeWorks,
  OASiS: PmsType.Oasis,
  Dentrix: PmsType.Dentrix,
};

// TODO: will be expanded to include ImagingSoftwareType and DocumentUploadType in a follow-up PR
export type UploadType = PmsType;

export interface IValidateUploadCodeRequest {
  code: string;
}

export interface IValidateUploadCodeResponse {
  customToken: string;
  sessionId: string;
  uploadPath: string;
  convertedPath?: string;
  sourcePath?: string;
}

export interface IFileUploadRequest {
  name: string;
  size: number;
  contentType: string;
}

export interface ISignedUploadUrl {
  name: string;
  /**
   * GCS V4 signed URL for a resumable upload session, generated server-side.
   */
  signedUrl: string;
  /**
   * URL expiry as a Unix timestamp in seconds, mirroring GCS.
   */
  expiresAt: number;
}

export interface IGetUploadSessionRequest {
  sessionId: string;
  files?: IFileUploadRequest[];
  uploadTarget?: DataUploadTarget;
}

export interface IGetUploadSessionResponse {
  uploadUrls?: ISignedUploadUrl[];
}

export interface IBackupFile {
  name: string;
  size: number;
  path: string;
}

export interface IBackupManifest {
  sessionId: string;
  files: IBackupFile[];
  pmsType?: PmsType;
  skipConversion?: boolean;
}

export interface ICompleteUploadRequest {
  sessionId: string;
  manifest: IBackupManifest;
}

export const isCompleteUploadRequest =
  TypeGuard.interface<ICompleteUploadRequest>({
    sessionId: isString,
    manifest: isObject,
  });

export interface ICompleteUploadResponse {
  success: boolean;
  message?: string;
}

export interface IFailUploadRequest {
  sessionId: string;
  errorMessage: string;
}

export const BACKUP_MANIFEST_FILENAME = 'backup-manifest.json';

// Conversion session resolution (called by VM before conversion starts)

export interface IResolveConversionSessionRequest {
  sessionId: string;
}

export const isResolveConversionSessionRequest =
  TypeGuard.interface<IResolveConversionSessionRequest>({
    sessionId: isString,
  });

export interface IResolveConversionSessionResponse {
  bucketName: string;
  uploadPath: string;
  convertedPath: string;
  manifestPath: string;
  pmsType?: PmsType;
  projectId: string;
  zone: string;
  sourcePath?: string;
}

// Conversion manifest (uploaded after conversion completes, triggers next phase)

export interface IConvertedFile {
  name: string;
  size: number;
  path: string;
}

export interface IConversionManifest {
  sessionId: string;
  pmsType?: PmsType;
  files: IConvertedFile[];
  sourceManifestPath: string;
}

// Complete conversion (called by VM after conversion finishes)

export interface ICompleteConversionRequest {
  sessionId: string;
  success: boolean;
  csvPaths?: string[];
  failedFiles?: string[];
  errorMessage?: string;
}

export const isCompleteConversionRequest =
  TypeGuard.interface<ICompleteConversionRequest>({
    sessionId: isString,
    success: isBoolean,
    csvPaths: TypeGuard.undefinedOr(TypeGuard.arrayOf(isString)),
    failedFiles: TypeGuard.undefinedOr(TypeGuard.arrayOf(isString)),
    errorMessage: TypeGuard.undefinedOr(isString),
  });

export interface IRetryConversionRequest {
  sessionId: string;
}

export const isRetryConversionRequest =
  TypeGuard.interface<IRetryConversionRequest>({
    sessionId: isString,
  });

export interface IRetryConversionResponse {
  success: boolean;
}

export interface IGetConversionLogRequest {
  sessionId: string;
}

export interface IGetConversionLogResponse {
  logContent: string;
}

export interface IGetUploadedFilesRequest {
  sessionId: string;
  uploadTarget?: DataUploadTarget;
  pageToken?: string;
}

export interface IGetUploadedFilesResponse {
  files: string[];
  nextPageToken?: string;
}
